import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '@tcl-boron-prefabs/button';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Success } from '@tcl-boron-icons/icons';
import { SuccessPalette } from '@tcl-boron-colors/colors';
import config from '../config';
import { trackEvent } from '../core/api/Pendo';

const stripePromise = loadStripe(config.stripe.publishableKey);

type Props = {
  onComplete: () => void;
};

const SetupForm: React.FC<Props> = ({ onComplete }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    const { error } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        // Only used if redirect flow is required
        return_url: window.location.href,
      },
    });

    setLoading(false);

    // If there are errors, the payments element automatically renders them
    if (!error) {
      trackEvent('PaymentMethodAdded');
      setSubmitted(true);
      onComplete();
    } else {
      trackEvent('PaymentMethodAddFailed', {
        error: error.message || 'Unknown error',
        errorType: error.type,
      });
    }
  };

  if (submitted) {
    return (
      <div className={css(styles.paymentCollectedContainer)}>
        <div className={css(styles.successMessage)}>
          <Success color={SuccessPalette[700]} />
          <p style={{ margin: 0 }}>
            <strong>Your card has been saved</strong>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div onSubmit={handleSubmit} className={css(styles.setupPaymentContainer)}>
      <PaymentElement />
      <Button
        ariaLabel={'Pay With This Card'}
        onClick={handleSubmit}
        disabled={!stripe || loading || submitted}
        data-pendo="stripe-payment-submit-button"
      >
        {loading ? 'Processing…' : 'Pay With This Card'}
      </Button>
    </div>
  );
};

const StripeWidget: React.FC<{ checkoutSessionSecret: string; onComplete: () => void }> = ({
  checkoutSessionSecret,
  onComplete,
}) => {
  return (
    <Elements stripe={stripePromise} options={{ clientSecret: checkoutSessionSecret }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
        <SetupForm onComplete={onComplete} />
      </div>
    </Elements>
  );
};

const styles = StyleSheet.create({
  setupPaymentContainer: {
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    '@media (min-width: 700px)': {
      width: '700px',
    },
  },
  paymentCollectedContainer: {
    textAlign: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
  },
  successMessage: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
  },
});

export default StripeWidget;
